import { AnimatePresence, motion } from 'framer-motion'
import { HandIcon } from 'lucide-react'
import { FC, useMemo } from 'react'

import { FireElement } from 'assets/game/elements/fire/fire-element'
import { LeafElement } from 'assets/game/elements/leaf/leaf-element'
import { WaterElement } from 'assets/game/elements/water/water-element'

import { aiFakeNames, aiOfficialNames } from 'features/ai/ai-names'
import { HandPiece } from 'features/board/board'
import { getPieceComponent, isFirePiece, isKingPiece, isLeafPiece, isWaterPiece } from 'features/board/utils'
import { playerOrbs } from 'features/board/utils'
import { Player } from 'features/firebase/types'
import { useSoundContext } from 'features/sound/sound-context'
import { DEFAULT_RATING } from 'features/user/elo-rating'

import { Badge } from 'shared/components/ui/badge'
import { cn } from 'shared/utils'

import { useGameEventContext } from '../event-context'
import { RelativePlayerPosition, useGameContext } from '../game-context'
import { getGameNumber } from '../utils'
import './player-hand.css'
import { Rating } from './rating'
import { RatingChange } from './rating-change'
import { Timer } from './timer'

interface PlayerHandProps {
  playerInfo: Player | null
  position: RelativePlayerPosition
}

const elementData = [
  { isPiece: isFirePiece, Element: FireElement, type: 'f' as HandPiece },
  { isPiece: isLeafPiece, Element: LeafElement, type: 'l' as HandPiece },
  { isPiece: isWaterPiece, Element: WaterElement, type: 'w' as HandPiece },
]

export const PlayerHand: FC<PlayerHandProps> = ({ playerInfo, position }) => {
  const { handPieceSelectedIdx, pieceSelected, handleHandPieceIdxClick, handleHandClick } = useGameEventContext()
  const { playClickSound } = useSoundContext()
  const {
    game: { ratings, gameId, ai, autoAddAI },
    turn,
    relativeGameData: { playerColor, hand },
  } = useGameContext()

  const handPieces = hand[position]
  const KingOrb = playerOrbs[playerColor[position]].king
  const ratingChange = ratings.after && ratings.after[playerColor[position]] - ratings.before[playerColor[position]]
  const highlight = turn === playerColor[position]

  const playerName = useMemo(() => {
    if (position === 'top' && ai) {
      if (autoAddAI) {
        return aiFakeNames[getGameNumber(gameId) % aiFakeNames.length]
      }

      return aiOfficialNames[getGameNumber(gameId) % aiOfficialNames.length]
    }

    return playerInfo?.nickname || playerInfo?.displayName
  }, [ai, position, playerInfo, autoAddAI, gameId])

  const isSelected = (idx: number, isPiece: (piece: HandPiece) => boolean) =>
    position === 'bottom' && handPieceSelectedIdx === idx && isPiece(hand.bottom[idx])

  const handPieceClick = (piece: HandPiece) => {
    if (position === 'top') return
    const playerColorPiece = playerColor.bottom === 'black' ? (piece.toUpperCase() as HandPiece) : piece
    const idx = hand.bottom.indexOf(playerColorPiece)
    handleHandPieceIdxClick(idx)
    playClickSound()
  }

  const SelectedPiece =
    position === 'bottom' && pieceSelected && !isKingPiece(pieceSelected) && getPieceComponent(pieceSelected)

  return (
    <motion.div
      layout
      data-active={highlight}
      className={cn(
        'mx-3 mb-2 grid items-center gap-2 rounded-lg px-4 py-1 transition-all data-[active=true]:bg-white data-[active=true]:bg-opacity-30 data-[active=true]:backdrop-blur-sm sm:mx-8 sm:gap-4',
        position === 'top' ? 'grid-areas-player-hand-top' : 'grid-areas-player-hand'
      )}
    >
      <div className="king-orb">
        <KingOrb className="h-12 sm:h-16" />
      </div>
      <div className="player-info mx-5 flex flex-col">
        <div className="flex items-center gap-2">
          <Rating rating={playerInfo?.rating || DEFAULT_RATING} />
          {ratingChange !== undefined && <RatingChange change={ratingChange} />}
        </div>
        <span className="font-bold lg:text-2xl">{playerName}</span>
      </div>
      <div className="hand-pieces flex h-10 w-full flex-1 items-center justify-center gap-2">
        <motion.div className="flex items-center justify-center gap-2">
          <AnimatePresence>
            {elementData.map(({ isPiece, Element, type }, idx) => {
              const count = handPieces.filter(isPiece).length
              const selected = handPieces.some((_, idx) => isSelected(idx, isPiece))

              return (
                count > 0 && (
                  <motion.div
                    key={idx}
                    data-selected={selected}
                    onClick={() => handPieceClick(type)}
                    className="relative cursor-pointer"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Element className={cn('h-12 w-auto transition-all sm:h-14', selected && 'h-[5rem] sm:h-[90px]')} />
                    <Badge variant="secondary" className="absolute bottom-0 right-0 h-4">
                      {count}
                    </Badge>
                  </motion.div>
                )
              )
            })}
          </AnimatePresence>
        </motion.div>
        <AnimatePresence>
          {SelectedPiece && (
            <motion.div
              className="relative flex cursor-pointer items-center justify-center"
              onClick={handleHandClick}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
            >
              <HandIcon className="absolute top-1/2 translate-y-[-65%]" size={30} color="black" />
              <SelectedPiece size={70} className="opacity-50" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="timer">
        <Timer position={position} playerInfo={playerInfo} />
      </div>
    </motion.div>
  )
}
